import { useEffect, useRef, useState, useCallback, ReactNode } from "react";

interface CustomScrollContainerProps {
  children: ReactNode;
  className?: string;
  direction?: "horizontal" | "vertical";
  showScrollbar?: "always" | "auto";
  scrollbarClassName?: string;
  containerClassName?: string;
  scrollbarColors?: {
    track?: string;
    thumb?: string;
    thumbHover?: string;
  };
  scrollbarSize?: number;
}

const CustomScrollContainer: React.FC<CustomScrollContainerProps> = ({
  children,
  className = "",
  direction = "horizontal",
  showScrollbar = "auto",
  scrollbarClassName = "",
  containerClassName = "",
  scrollbarColors = {
    track: "bg-gray-100",
    thumb: "bg-gray-400",
    thumbHover: "hover:bg-gray-500",
  },
  scrollbarSize = 8,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [thumbPosition, setThumbPosition] = useState(0);
  const [thumbSize, setThumbSize] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);

  const isHorizontal = direction === "horizontal";

  const updateScrollbar = useCallback(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;

    if (isHorizontal) {
      const overflow = container.scrollWidth > container.clientWidth;
      setHasOverflow(overflow);

      if (overflow) {
        const thumbWidth =
          (container.clientWidth / container.scrollWidth) *
          container.clientWidth;
        const thumbLeft =
          (container.scrollLeft / container.scrollWidth) *
          container.clientWidth;
        setThumbSize(thumbWidth);
        setThumbPosition(thumbLeft);
      }
    } else {
      const overflow = container.scrollHeight > container.clientHeight;
      setHasOverflow(overflow);

      if (overflow) {
        const { scrollHeight, clientHeight, scrollTop } = container;
        const thumbHeight = Math.max(
          (clientHeight / scrollHeight) * clientHeight,
          24
        );
        const maxScroll = scrollHeight - clientHeight;
        const maxThumbTop = Math.max(clientHeight - thumbHeight, 0);
        const thumbTop =
          maxScroll > 0 ? (scrollTop / maxScroll) * maxThumbTop : 0;
        setThumbSize(thumbHeight);
        setThumbPosition(thumbTop);
      }
    }
  }, [isHorizontal]);

  const handleScroll = () => {
    updateScrollbar();
  };

  const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const trackRect = e.currentTarget.getBoundingClientRect();

    if (isHorizontal) {
      const clickPosition = e.clientX - trackRect.left;
      const scrollRatio = clickPosition / trackRect.width;
      container.scrollLeft = scrollRatio * container.scrollWidth;
    } else {
      const clickPosition = e.clientY - trackRect.top;
      const { scrollHeight, clientHeight } = container;
      const maxScroll = scrollHeight - clientHeight;
      const scrollRatio = clickPosition / trackRect.height;
      container.scrollTop = scrollRatio * maxScroll;
    }
  };

  useEffect(() => {
    updateScrollbar();
    window.addEventListener("resize", updateScrollbar);

    const observer = new ResizeObserver(updateScrollbar);
    const el = scrollContainerRef.current;
    if (el) {
      observer.observe(el);
      for (const child of el.children) {
        observer.observe(child);
      }
    }

    return () => {
      window.removeEventListener("resize", updateScrollbar);
      observer.disconnect();
    };
  }, [updateScrollbar]);

  const shouldShowScrollbar =
    showScrollbar === "always" || (showScrollbar === "auto" && hasOverflow);

  return (
    <div
      className={`relative ${containerClassName} ${
        isHorizontal ? "" : "flex min-h-0 flex-row items-stretch"
      }`}
    >
      <style>{`
        .custom-scroll-hide::-webkit-scrollbar {
          display: none;
        }
        .custom-scroll-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={`custom-scroll-hide ${
          isHorizontal
            ? "overflow-x-auto overflow-y-hidden"
            : "min-h-0 min-w-0 overflow-y-auto overflow-x-hidden flex-1"
        } ${className}`}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {children}
      </div>

      {shouldShowScrollbar && (
        <div
          className={`${
            isHorizontal
              ? `w-full mt-2`
              : `relative ml-2 shrink-0 overflow-hidden self-stretch`
          } ${scrollbarColors.track} rounded-full cursor-pointer ${scrollbarClassName}`}
          style={
            isHorizontal
              ? { height: `${scrollbarSize}px` }
              : { width: `${scrollbarSize}px` }
          }
          onClick={handleScrollbarClick}
          role="presentation"
          aria-hidden
        >
          <div
            className={`${scrollbarColors.thumb} ${scrollbarColors.thumbHover} rounded-full transition-all duration-150 ${
              isHorizontal ? "" : "absolute left-0 right-0"
            }`}
            style={
              isHorizontal
                ? {
                    height: "100%",
                    width: `${thumbSize}px`,
                    transform: `translateX(${thumbPosition}px)`,
                  }
                : {
                    width: "100%",
                    height: `${thumbSize}px`,
                    transform: `translateY(${thumbPosition}px)`,
                  }
            }
          />
        </div>
      )}
    </div>
  );
};

export default CustomScrollContainer;
